import { useEffect, useState, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Triangle, ScreenShare, Users } from "lucide-react";

import { SECTION_DEFS } from "../constants";
import { useLayout } from "../../contexts/LayoutContext";

const CHAT_ADD_BUTTONS = [
  {
    key: "private",
    label: "Private",
    Icon: Triangle,
    colorClass: "text-velBlue",
  },
  {
    key: "public",
    label: "Public",
    Icon: ScreenShare,
    colorClass: "text-[#912323]",
  },
  { key: "team", label: "Team", Icon: Users, colorClass: "text-activeCase" },
];

const NavBreadcrumb = () => {
  const { currentAppPage } = useLayout();
  const navigate = useNavigate();
  const [sectionDropdownOpen, setSectionDropdownOpen] = useState(false);
  const [subDropdownOpen, setSubDropdownOpen] = useState(false);
  const [breadcrumbInfo, setBreadcrumbInfo] = useState({
    subLabel: null,
    subIcon: null,
    hasAdd: false,
    addKind: null,
    addLabel: null,
  });

  const sectionRef = useRef(null);
  const subRef = useRef(null);

  const currentSection = useMemo(
    () =>
      SECTION_DEFS.find((s) => s.path === currentAppPage) || SECTION_DEFS[0],
    [currentAppPage],
  );

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === currentAppPage) {
        setBreadcrumbInfo({
          subLabel: e.detail.subLabel || null,
          subIcon: e.detail.subIcon || null,
          hasAdd: !!e.detail.hasAdd,
          addKind: e.detail.addKind || null,
          addLabel: e.detail.addLabel || null,
        });
      }
    };
    window.addEventListener("breadcrumb:update", handler);
    return () => window.removeEventListener("breadcrumb:update", handler);
  }, [currentAppPage]);

  useEffect(() => {
    setBreadcrumbInfo({
      subLabel: null,
      subIcon: null,
      hasAdd: false,
      addKind: null,
      addLabel: null,
    });
    setSectionDropdownOpen(false);
    setSubDropdownOpen(false);
    window.dispatchEvent(
      new CustomEvent("breadcrumb:request", {
        detail: { section: currentAppPage },
      }),
    );
  }, [currentAppPage]);

  useEffect(() => {
    if (!sectionDropdownOpen && !subDropdownOpen) return;
    const onDoc = (e) => {
      if (e.button === 2 || e.__contextMenuHandled) return;
      if (e.target?.closest?.('[role="menu"]')) return;

      if (
        sectionDropdownOpen &&
        sectionRef.current &&
        !sectionRef.current.contains(e.target)
      )
        setSectionDropdownOpen(false);
      if (
        subDropdownOpen &&
        subRef.current &&
        !subRef.current.contains(e.target)
      )
        setSubDropdownOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSectionDropdownOpen(false);
        setSubDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [sectionDropdownOpen, subDropdownOpen]);

  const handleSectionSelect = (path) => {
    navigate(path);
    setSectionDropdownOpen(false);
    setSubDropdownOpen(false);
  };

  const handleAddClick = (e) => {
    e.stopPropagation();
    if (breadcrumbInfo.addKind) {
      window.dispatchEvent(
        new CustomEvent("breadcrumb:add", {
          detail: { section: currentAppPage },
        }),
      );
    }
  };

  const CurIcon = currentSection.Icon;

  const crumbBtnClass = [
    "flex items-center gap-x-1.5 rounded-lg border px-2.5 py-1.5",
    "text-[13px] font-light tracking-wide",
    "transition-all duration-200 cursor-pointer select-none",
  ].join(" ");

  const portalSlotId = (path) => {
    const name = path.replace(/^\//, "");
    return `${name}-navbar-portal-slot`;
  };

  return (
    <div className="flex items-center gap-x-0.5">
      <div ref={sectionRef} className="relative">
        <button
          onClick={() => {
            setSectionDropdownOpen((v) => !v);
            setSubDropdownOpen(false);
          }}
          className={[
            crumbBtnClass,
            sectionDropdownOpen
              ? "bg-velBlue/15 border-velBlue/30 text-velBlue"
              : "bg-transparent border-transparent text-mutedText hover:bg-btnHover hover:border-velBlueSecond/25",
          ].join(" ")}
        >
          <CurIcon
            strokeWidth={1.5}
            className={[
              "h-4 w-4 shrink-0",
              sectionDropdownOpen ? "text-velBlue" : "text-mutedText/60",
            ].join(" ")}
          />
          <span>{currentSection.label}</span>
        </button>

        {sectionDropdownOpen && (
          <div className="absolute left-0 top-full z-50 mt-1.5 min-w-[200px]">
            <div className="rounded-lg border border-surfaceBorder bg-dropdownBg/95 p-1.5 backdrop-blur-md shadow-login-panel">
              {SECTION_DEFS.map((s) => {
                const isActive = s.path === currentAppPage;
                const SIcon = s.Icon;
                return (
                  <Link
                    key={s.path}
                    to={s.path}
                    onClick={() => {
                      setSectionDropdownOpen(false);
                      setSubDropdownOpen(false);
                    }}
                    className={[
                      "w-full flex items-center gap-x-2.5 rounded-lg border border-transparent px-3 py-2 text-[13px] font-light transition-all duration-200",
                      isActive
                        ? "bg-velBlue/10 text-velBlue"
                        : "text-mutedText hover:bg-dropdownItemHover hover:border-velBlueSecond/25",
                    ].join(" ")}
                  >
                    <SIcon
                      strokeWidth={1.5}
                      className={[
                        "h-4 w-4 shrink-0",
                        isActive ? "text-velBlue" : "text-mutedText/60",
                      ].join(" ")}
                    />
                    <span>{s.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {breadcrumbInfo.hasAdd && breadcrumbInfo.addKind === "chat" ? (
        <div className="flex items-center gap-x-0.5">
          {CHAT_ADD_BUTTONS.map(({ key, label, Icon, colorClass }) => (
            <button
              key={key}
              onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(
                  new CustomEvent("breadcrumb:add", {
                    detail: { section: currentAppPage, chatKind: key },
                  }),
                );
              }}
              className={[
                "flex items-center justify-center w-6 h-6 rounded-md border border-transparent",
                "opacity-60 transition-all duration-200 cursor-pointer",
                "hover:opacity-100 hover:bg-velBlue/10 hover:border-velBlue/20 hover:scale-110",
                colorClass,
              ].join(" ")}
              title={`New ${label.toLowerCase()} thread`}
            >
              <Icon strokeWidth={2} className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      ) : breadcrumbInfo.hasAdd ? (
        <button
          onClick={handleAddClick}
          className="flex items-center gap-x-1 rounded-md border border-transparent px-1.5 py-1 text-mutedText/70 transition-all duration-200 cursor-pointer hover:text-velBlue hover:bg-velBlue/10 hover:border-velBlue/20"
          title={
            breadcrumbInfo.addLabel ||
            `Add new ${currentSection.label.toLowerCase()} item`
          }
        >
          <Plus strokeWidth={2} className="h-3.5 w-3.5" />
          {breadcrumbInfo.addLabel && (
            <span className="text-[11px] tracking-wide">
              {breadcrumbInfo.addLabel}
            </span>
          )}
        </button>
      ) : null}

      {currentSection.hasSubItems && (
        <div ref={subRef} className="relative">
          <button
            onClick={() => {
              setSubDropdownOpen((v) => !v);
              setSectionDropdownOpen(false);
            }}
            className={[
              crumbBtnClass,
              "max-w-[220px]",
              subDropdownOpen
                ? "bg-velBlue/15 border-velBlue/30 text-velBlue"
                : "bg-transparent border-transparent text-mutedText hover:bg-btnHover hover:border-velBlueSecond/25",
            ].join(" ")}
          >
            <span className="truncate">
              {breadcrumbInfo.subLabel || "Select…"}
            </span>
          </button>

          {subDropdownOpen && (
            <div className="absolute left-0 top-full z-50 mt-1.5 min-w-[240px] max-w-[320px]">
              <div className="rounded-lg border border-surfaceBorder bg-dropdownBg/95 p-1.5 max-h-[60vh] overflow-y-auto backdrop-blur-md shadow-login-panel">
                <div id={portalSlotId(currentAppPage)} data-navbar="true" />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NavBreadcrumb;
